import React from 'react';
import { alpha, Box, Stack, Theme, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { MoveBlockState } from './EditPanelBridge';

function bodyKey(block: MoveBlockState): string {
    const gates =
        block.inconsistencies && block.clarifications
            ? 'both'
            : block.inconsistencies
              ? 'inconsistencies'
              : 'clarifications';

    return block.canResolve ? `move_blocked_${gates}` : `move_blocked_no_access_${gates}`;
}

// §3.1 move-blocked banner: no dismiss button — the host hides it on tab change.
// Re-mount with a new key (attempt) to re-run the shake animation.
export default function MoveBlockedBanner({ block }: { block: MoveBlockState }) {
    const { t } = useBundleTranslation('app/content-workflow/element-editor');

    return (
        <Stack
            data-test="move_blocked_banner"
            role="alert"
            aria-live="assertive"
            direction="row"
            spacing={1.5}
            sx={{
                p: 1.5,
                borderRadius: '3px',
                borderLeft: '3px solid',
                borderColor: 'error.main',
                bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.08),
                '@keyframes miMoveBlockedShake': {
                    '10%, 90%': { transform: 'translateX(-1px)' },
                    '20%, 80%': { transform: 'translateX(2px)' },
                    '30%, 50%, 70%': { transform: 'translateX(-3px)' },
                    '40%, 60%': { transform: 'translateX(3px)' },
                },
                animation: 'miMoveBlockedShake 0.5s ease',
            }}
        >
            <IconMi icon="alert" sx={{ color: 'error.main', fontSize: '16px', mt: '2px', flexShrink: 0 }} />
            <Box>
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}>
                    {t('move_blocked_title')}
                </Typography>{' '}
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.primary' }}>
                    {t(bodyKey(block))}
                    {!block.canResolve && ` ${t('move_blocked_ask_admin')}`}
                </Typography>
            </Box>
        </Stack>
    );
}
